import classnames from 'classnames';
import { useContext, useRef } from 'preact/hooks';
import { useArrowKeyNavigation } from '../../hooks/use-arrow-key-navigation';
import { RadioCheckedIcon, RadioIcon } from '../icons';
import RadioGroupContext from './RadioGroupContext';
import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
function Radio({
  value,
  children,
  subtitle,
  disabled: radioDisabled
}) {
  const radioGroupContext = useContext(RadioGroupContext);
  if (!radioGroupContext) {
    throw new Error('RadioGroup.Radio can only be used as RadioGroup child');
  }
  const {
    selected,
    disabled = radioDisabled,
    onChange
  } = radioGroupContext;
  const isSelected = !disabled && selected === value;
  return _jsxs("div", {
    role: "radio",
    "aria-checked": isSelected,
    "aria-disabled": disabled,
    className: classnames('focus-visible-ring rounded-lg px-3 py-2 flex-1 group', {
      'hover:bg-grey-3/25 aria-checked:bg-grey-3/50 cursor-pointer': !disabled,
      'opacity-70': disabled
    }),
    "data-value": value,
    onClick: !disabled ? () => onChange(value) : undefined,
    onKeyDown: disabled ? undefined : e => {
      if (['Enter', ' '].includes(e.key)) {
        e.preventDefault();
        onChange(value);
      }
    },
    tabIndex: -1,
    children: [_jsxs("div", {
      className: "flex items-center gap-x-1.5",
      children: [isSelected ? _jsx(RadioCheckedIcon, {}) : _jsx(RadioIcon, {}), _jsx("span", {
        className: classnames('flex items-center gap-x-1.5', {
          'text-grey-7 group-hover:text-grey-8 group-aria-checked:text-grey-8': !disabled
        }),
        children: children
      })]
    }), subtitle && _jsx("div", {
      className: classnames('pl-4 ml-1.5 mt-1 text-sm', {
        'text-grey-6 group-hover:text-grey-7 group-aria-checked:text-grey-7': !disabled
      }),
      children: subtitle
    })]
  });
}
Radio.displayName = 'RadioGroup.Radio';
function RadioGroupMain({
  direction = 'horizontal',
  children,
  selected,
  onChange,
  disabled,
  'aria-label': label,
  'aria-labelledby': labelledBy,
  name
}) {
  const containerRef = useRef(null);
  useArrowKeyNavigation(containerRef, {
    loop: false,
    selector: '[role="radio"]:not([aria-disabled="true"])',
    focusElement: el => {
      onChange(el.dataset.value);
      el.focus();
    }
  });
  return _jsxs(RadioGroupContext.Provider, {
    value: {
      selected,
      disabled,
      onChange: onChange
    },
    children: [_jsx("div", {
      "aria-label": label,
      "aria-labelledby": labelledBy,
      ref: containerRef,
      role: "radiogroup",
      className: classnames('w-full flex gap-1.5', {
        'flex-col': direction === 'vertical'
      }),
      children: children
    }), name && _jsx("input", {
      type: "hidden",
      "data-testid": "hidden-input",
      name: name,
      value: selected,
      disabled: disabled
    })]
  });
}
const RadioGroup = Object.assign(RadioGroupMain, {
  Radio,
  displayName: 'RadioGroup'
});
export default RadioGroup;
//# sourceMappingURL=RadioGroup.js.map